import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef, ReactElement } from 'react';

import type { ListboxProps } from './listbox.types';

import './listbox.scss';

/**
 * @internal
 * The `Listbox` component is used to display a list of items aligned with design system styles.
 * Use the `ListboxItem` component to define the items of the list.
 *
 * ### Usage
 *
 * ```tsx
 * import { Listbox, ListboxItem } from '@bloomreach/react-banana-ui';
 *
 * export default function MyMenu() {
 *   return (
 *      <Listbox>
 *        <ListboxItem>Item 1</ListboxItem>
 *        <ListboxItem>Item 2</ListboxItem>
 *        <ListboxItem>Item 3</ListboxItem>
 *      </Listbox>
 *   );
 * }
 * ```
 */
const Listbox = forwardRef((props: ListboxProps, forwardedRef: ForwardedRef<HTMLUListElement> | null): ReactElement => {
  const {
    children,
    className,
    role = 'listbox',
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  return (
    <ul className={cn('rbui-listbox', className)} ref={forwardedRef} role={role} {...rest}>
      {children}
    </ul>
  );
});

Listbox.displayName = 'Listbox';

export default Listbox;
